import {Grid} from '@material-ui/core';
import {Map as IMap, List} from 'immutable';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {removeQuoteFormErrorEntries, removeQuoteFormErrorEntry, updateQuoteFormErrors} from 'Quoting/ducks/createQuote';
import {createEisTypeObject, createUniqueFormFieldId, VALUE_NOT_AVAILABLE} from 'Quoting/utils/utilities';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid, GridRowProps} from '../../../../../shared/GridRow';
import {QuoteFormElement} from '../../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../../shared/QuoteFormWrapper';
import {
  GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID,
  GPLUCoverageCode,
  spouseOrder,
  GPLU_SPOUSE_TYPE,
} from '../../../PlanTemplates/GWL/gpluConstants';
import {
  createSpouseUnderwritingAmounts,
  createSpouseWLCoverageBenefitSchedules,
} from '../../../PlanTemplates/GWL/Plan1';
import {getParentPath, getSpouseCoverageDefinitionPropertyPaths, spouseTypeInfo} from '../PropertyPaths';

export const GPLUSpouseCoverageDefinitionsBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {selectedPlan, defaultPlanValues, selectedCoverageIndex, quoteFormErrors} = useSelector(
    (state: ReducerState) => state.quoteForm
  );

  const dispatch = useDispatch();

  const spouseBenefitSchedulesPath = `${getParentPath(
    selectedPlan?.planIndex,
    selectedCoverageIndex
  )}.benefitSchedules`;

  const spouseUnderwritingPath = `${getParentPath(
    selectedPlan?.planIndex,
    selectedCoverageIndex
  )}.underwriting.spouseUnderwritingAmounts`;

  const selectedPlanCd =
    getQuoteFieldValue<string>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].planCd`) ??
    VALUE_NOT_AVAILABLE;

  const defaultValues = defaultPlanValues?.get(selectedPlanCd)?.get(GPLUCoverageCode.SPOUSE_WHOLE_LIFE);
  const propertyPaths = getSpouseCoverageDefinitionPropertyPaths(selectedPlan?.planIndex ?? 0);
  const selectedPlanKey: any = getQuoteFieldValue(
    `quote.benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key`
  );
  const [rootId, parentId] = [selectedPlanKey?.get('rootId'), selectedPlanKey?.get('id')];

  const spouseWorkingOptions: List<IMap<string, any>> = getQuoteFieldValue(propertyPaths.spouseCoveredOption);

  const swlWhoIsCoveredFormFieldId = createUniqueFormFieldId(
    GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.spouseCoveredOption,
    selectedPlan?.planKey
  );

  const [coveredOptions, setCoveredOptions] = useState(
    spouseWorkingOptions
      ?.map((m: IMap<string, any>) => String(m.get('spouseWorkingOptionsCd')))
      .filter(Boolean)
      .toArray() ?? []
  );
  const benefitSchedulesParentKey: any = getQuoteFieldValue(
    `${getParentPath(selectedPlan?.planIndex, selectedCoverageIndex)}._key`
  );
  const [bs_rootId, bs_parentId] = [benefitSchedulesParentKey?.get('rootId'), benefitSchedulesParentKey?.get('id')];

  const underwritingKey: any = getQuoteFieldValue(
    `${getParentPath(selectedPlan?.planIndex, selectedCoverageIndex)}.underwriting._key`
  );
  const [uw_rootId, uw_parentId] = [underwritingKey?.get('rootId'), underwritingKey?.get('id')];

  const handleCoverageCodes = (newValues: string[]) => {
    if (newValues?.length === 0) {
      dispatch(
        updateQuoteFormErrors({
          [swlWhoIsCoveredFormFieldId]:
            "At least one option must be selected for 'Who is Covered?' under Spouse Whole Life",
        })
      );
    } else {
      dispatch(removeQuoteFormErrorEntry(swlWhoIsCoveredFormFieldId));
    }

    newValues.sort((a, b) => spouseOrder.indexOf(a) - spouseOrder.indexOf(b));
    setCoveredOptions(newValues);

    const lookup = new Map<string, IMap<string, any>>();

    spouseWorkingOptions?.forEach(m => {
      lookup.set(String(m.get('spouseWorkingOptionsCd')), m);
    });

    const updatedCoverageCds = List(
      newValues.map(code => {
        const key = code;
        return (
          lookup.get(key) ??
          IMap({
            ...createEisTypeObject('GPLUSpouseCoveredMultipleValues', rootId, parentId),
            spouseWorkingOptionsCd: key,
          })
        );
      })
    );

    const benefitSchedules: List<IMap<string, any>> | undefined[] =
      getQuoteFieldValue<List<IMap<string, any>>>(spouseBenefitSchedulesPath) ?? [];

    const updatedBenefitSchedules = newValues.map(type => {
      const schExists = benefitSchedules?.find(sch => sch.get('spouseType') === type);

      let noUpdateBenefitAmount = true;
      if (schExists && schExists.getIn(['rangeValues', 'benefitMaxAmount', 'amount'])) noUpdateBenefitAmount = true;
      else if (schExists && schExists.getIn(['singleValue', 'flatBenefitAmount', 'amount']))
        noUpdateBenefitAmount = true;

      return schExists && noUpdateBenefitAmount
        ? schExists
        : IMap(
            createSpouseWLCoverageBenefitSchedules(defaultValues, bs_rootId, bs_parentId, spouseTypeInfo[type].suffix)
          );
    });

    const SpouseUnderwriting: List<IMap<string, any>> | undefined[] =
      getQuoteFieldValue<List<IMap<string, any>>>(spouseUnderwritingPath) ?? [];

    const updatedSpouseUnderwriting = newValues.map(type => {
      const uwExists = SpouseUnderwriting?.find(suw => suw.get('_type') === spouseTypeInfo[type].uwType);
      return uwExists && uwExists.getIn(['guaranteedIssueAmount', 'amount'])
        ? uwExists
        : IMap(createSpouseUnderwritingAmounts(defaultValues, spouseTypeInfo[type].uwType, uw_rootId, uw_parentId));
    });

    updateQuoteFieldValues([
      {path: propertyPaths.spouseCoveredOption, newValue: updatedCoverageCds},
      {path: spouseBenefitSchedulesPath, newValue: updatedBenefitSchedules},
      {path: spouseUnderwritingPath, newValue: updatedSpouseUnderwriting},
    ]);

    if (!newValues.includes(GPLU_SPOUSE_TYPE.WS)) {
      const globalErrorsToRemove = Object.keys(quoteFormErrors)?.filter(
        key =>
          key.includes(selectedPlan?.planKey) &&
          Object.values(GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.WS).some(wsKey => key.includes(wsKey))
      );
      dispatch(removeQuoteFormErrorEntries(globalErrorsToRemove));
    }

    if (!newValues.includes(GPLU_SPOUSE_TYPE.NWS)) {
      const globalErrorsToRemove = Object.keys(quoteFormErrors)?.filter(
        key =>
          key.includes(selectedPlan?.planKey) &&
          Object.values(GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.NWS).some(nwsKey => key.includes(nwsKey))
      );
      dispatch(removeQuoteFormErrorEntries(globalErrorsToRemove));
    }
  };

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'coverage-definitions-row-1'},
      items: [
        {
          key: propertyPaths.spouseCoveredOption,
          element: (
            <QuoteFormElement
              elementType="MultiSelect"
              dropdownVariant="AttributeValue"
              searchKey="GPLUSpouseCoveredMultipleValues.spouseWorkingOptionsCd"
              label="Who is Covered?"
              value={coveredOptions}
              id={propertyPaths.spouseCoveredOption}
              onChange={val => handleCoverageCodes(val)}
              coverageCode={GPLUCoverageCode.SPOUSE_WHOLE_LIFE}
              formFieldId={GPLU_SPOUSE_WHOLE_LIFE_FIELD_ID.spouseCoveredOption}
              required
            />
          ),
          props: {xs: 12, sm: 12, md: 6, lg: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Coverage Definition" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
